/**
 * The engine's number of rate shops per day (CU-869fhuhy6, criteria
 * 27-29, UX D5): never a formula typed in the front.
 *
 *   <RateShopsProvider>            once per page (Strategies, Executions)
 *   useTenantRateShops()           → { data, loading, error, reload, setWarning }
 *                                    `data` is `GET /strategies/rate-shops` as the
 *                                    server sends it: `per_strategy`, `grouped_total`,
 *                                    `plain_total`, `alignment_hints`, `warning`,
 *                                    `tethers` (the engine's fit of every tether per
 *                                    strategy, CU-869fkr2yp)
 *   useRateShops(draft, id)        → { estimate, loading, error, data } for the
 *                                    wizard: `POST /strategies/rate-shops` with the
 *                                    draft as it stands, 400 ms after the last change
 *                                    of a field that enters the count (criterion 27)
 *   <DraftRateShopsProvider>       once per drawer: `useRateShops` on the drawer's
 *   useDraftRateShops()              draft (valid cadence and a product), shared by
 *                                    When does it run and the Pickup dates line of
 *                                    When does it apply — one request, not two
 *
 * The breakdown is painted as the engine sends it (`breakdown.offices ×
 * pickup_dates × runs_per_day`; sites and LORs travel inside one search):
 * no multiplication happens here.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { strategiesApi } from 'Pages/DsRepricing/Page/@services/api'
import { setupApi } from 'Pages/DsRepricing/Setup/@services/api'
import { toServer } from 'Pages/DsRepricing/Page/@services/adapters'
import { transformData } from 'Pages/DsRepricing/Page/@hooks/useFormSubmit'
import { dsRepricingDrawerData } from 'Pages/DsRepricing/Page/@signals'
import { cadenceErrors, factoryCadence } from 'Pages/DsRepricing/Page/@services/cadence'

const DEBOUNCE_MS = 400

const EMPTY = { data: null, loading: true, error: null, reload: () => {}, setWarning: async () => {} }
const RateShopsContext = createContext(EMPTY)

export function RateShopsProvider({ children }) {
	const [state, setState] = useState({ data: null, loading: true, error: null })
	const reload = useCallback(async ({ quiet = false } = {}) => {
		if (!quiet) setState(s => ({ ...s, loading: true }))
		try {
			const data = await strategiesApi.rateShops.get()
			setState({ data, loading: false, error: null })
		} catch (e) {
			setState(s => ({ data: s.data, loading: false, error: e?.message || 'Could not read the rate shops' }))
		}
	}, [])
	useEffect(() => { reload() }, [reload])
	// The threshold lives in the panel where it is read (UX v3 D21); saved
	// through Setup, then the figures are reread so the amber state follows.
	const setWarning = useCallback(async (warning, reason) => {
		await setupApi.rateShopsWarning(warning, reason)
		setState(s => ({ ...s, data: s.data ? { ...s.data, warning } : s.data }))
		reload({ quiet: true })
	}, [reload])
	const value = useMemo(() => ({ ...state, reload, setWarning }), [state, reload, setWarning])
	return <RateShopsContext.Provider value={value}>{children}</RateShopsContext.Provider>
}

export function useTenantRateShops() {
	return useContext(RateShopsContext)
}

/** The engine's entry for one strategy of the tenant answer, or null. */
export function perStrategy(data, id) {
	if (!data?.per_strategy || id == null) return null
	return data.per_strategy[String(id)] || null
}

//: What enters the count (criterion 27): the product, the cadence, the
//: period, the durations, the offices and the sites — and, since the
//: estimate also says where each tether applies (CU-869fkr2yp, criterion
//: 11), the cars, comparing by category and the tethers chosen. A change
//: anywhere else — the name, the undercut — must not fire a request.
const countKey = (d) => JSON.stringify({
	p: d?.productId ?? null, w: d?.whenItRuns ?? null, pe: d?.period ?? null, du: d?.durations ?? null,
	o: d?.filterGroupOffices ?? null, ox: !!d?.filterGroupOfficesExclusion, s: d?.filterGroupSites ?? null,
	a: d?.filterAcriss ?? null, ax: !!d?.filterAcrissExclusion, cc: !!d?.compareByCategory, t: d?.tethers ?? null,
})

/**
 * The engine's count for the wizard's draft, recomputed without saving.
 * `draft` is `dsRepricingDrawerData.value`; `null` asks nothing.
 */
export function useRateShops(draft) {
	const key = countKey(draft)
	const id = draft?.id ?? null
	const [state, setState] = useState({ data: null, estimate: null, loading: false, error: null })
	const latest = useRef(0)
	const ask = useCallback(async (d) => {
		const ticket = ++latest.current
		setState(s => ({ ...s, loading: true }))
		try {
			const data = await strategiesApi.rateShops.check(toServer(transformData({ ...d, enabled: true })), d.id ?? null)
			if (ticket !== latest.current) return
			setState({ data, estimate: perStrategy(data, d.id ?? 'draft'), loading: false, error: null })
		} catch (e) {
			if (ticket !== latest.current) return
			setState({ data: null, estimate: null, loading: false, error: e?.message || 'The engine could not count the searches' })
		}
	}, [])
	useEffect(() => {
		if (!draft || !draft.productId) { setState({ data: null, estimate: null, loading: false, error: null }); return undefined }
		const t = setTimeout(() => ask(draft), DEBOUNCE_MS)
		return () => clearTimeout(t)
	}, [key, id])
	return { ...state, reload: () => draft && ask(draft) }
}

const IDLE = { data: null, estimate: null, loading: false, error: null, reload: () => {} }
const DraftRateShopsContext = createContext(IDLE)

/**
 * The engine's count for the drawer's draft, once (CU-869fkr1a9): asked
 * only with a valid cadence and a product, as When does it run always did.
 */
export function DraftRateShopsProvider({ children }) {
	const data = dsRepricingDrawerData.value || {}
	const valid = Object.keys(cadenceErrors(data.whenItRuns || factoryCadence())).length === 0
	const shop = useRateShops(valid && data.productId ? data : null)
	return <DraftRateShopsContext.Provider value={shop}>{children}</DraftRateShopsContext.Provider>
}

export function useDraftRateShops() {
	return useContext(DraftRateShopsContext)
}
